import React, { type PropsWithChildren, useMemo } from 'react';

import { type BootstrapState, type ClearReferrerCallback, initialState, type RefreshSearchSessionIdCallback } from '../../../../../common/constants/bootstrap';
import { ALL_FILTER_EXCLUDED_PRODUCTS } from '../../../../../common/constants/filters';
import { type BootstrapContainerProps, type QueryParamsCallback } from '../../index';
import { StaticBootstrapContext } from '../index';

type StaticContextProviderProps = BootstrapContainerProps & {
	refreshSearchSessionId: RefreshSearchSessionIdCallback;
	clearReferrer: ClearReferrerCallback;
	initialText: string | undefined;
}

export type StaticBootstrapContextState = Omit<
	BootstrapState,
	'queryParams' | 'referrer' | 'prevQueryParams' | 'queryParamsCallback' | 'searchSessionId'
> & {
	// Added the callback wasnt being stored in state (good) and now is in static context, and is not undefined, because we always set noop in state initialiser
	queryParamsCallback: QueryParamsCallback;
	refreshSearchSessionId: RefreshSearchSessionIdCallback;
	clearReferrer: ClearReferrerCallback;
	initialTextQueryParam: string | undefined;
};

// Attempting to build from the same underlying definitions to ease migration
export const defaultStaticContext = {
	...initialState,
	initialTextQueryParam: undefined,
	queryParamsCallback: () => {},
	refreshSearchSessionId: () => {},
	clearReferrer: () => {},
};

export const StaticContextProvider = ({
	children,
	initialProps: props,
	queryParamsCallback,
	refreshSearchSessionId,
	initialText,
	clearReferrer,
}: PropsWithChildren<StaticContextProviderProps>): React.JSX.Element => {
	// Do not change this! This is what enforces only a single rerender when config is ready
	const staticLock = props.isConfigLoaded;

	const useMemoStaticProps: StaticBootstrapContextState = useMemo(() => {
		return {
			cloudId: props.cloudId || defaultStaticContext.cloudId,
			aggregatorHostName: props.aggregatorHostName || defaultStaticContext.aggregatorHostName,
			aggAbsoluteUrl: props.aggAbsoluteUrl || defaultStaticContext.aggAbsoluteUrl,
			SAINHostName: props.SAINHostName || defaultStaticContext.SAINHostName,
			orgId: props.orgId || defaultStaticContext.orgId,
			primaryProduct: props.primaryProduct || defaultStaticContext.primaryProduct,
			analyticsAttributes: props.analyticsAttributes || defaultStaticContext.analyticsAttributes,
			features: props.features || defaultStaticContext.features,
			intercomHmac: props.intercomHmac || defaultStaticContext.intercomHmac,
			experimentConfig: props.experimentConfig || defaultStaticContext.experimentConfig,
			experimentLayers: props.experimentLayers || defaultStaticContext.experimentLayers,
			isNounsAvailable: props.isNounsAvailable || defaultStaticContext.isNounsAvailable,
			isRovoEnabled: props.isRovoEnabled || defaultStaticContext.isRovoEnabled,
			isConfigLoaded: props.isConfigLoaded || defaultStaticContext.isConfigLoaded,
			nouns: props.nouns || defaultStaticContext.nouns,
			allFilterExcludedProducts: ALL_FILTER_EXCLUDED_PRODUCTS,
			productEdition: props.productEdition || defaultStaticContext.productEdition,
			productDataRegions: props.productDataRegions || defaultStaticContext.productDataRegions,
			rovoFeatures: props.rovoFeatures || defaultStaticContext.rovoFeatures,
			linkedCloudARIs: props.linkedCloudARIs || defaultStaticContext.linkedCloudARIs,
			isJiraIssueIndexed: props.isJiraIssueIndexed || defaultStaticContext.isJiraIssueIndexed,
			user: props.user || defaultStaticContext.user,
			thirdPartyConfigs: props.thirdPartyConfigs || defaultStaticContext.thirdPartyConfigs,
			products: props.products || defaultStaticContext.products,
			initialSpaceARIs: props.initialSpaceARIs || defaultStaticContext.initialSpaceARIs,
			initialTextQueryParam: initialText,
			onSearchCompleted: props.onSearchCompleted || defaultStaticContext.onSearchCompleted,
			// Callbacks are considered statically defined too!
			queryParamsCallback: queryParamsCallback || defaultStaticContext.queryParamsCallback,
			// passed directly through
			refreshSearchSessionId,
			clearReferrer
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [staticLock]);

	return (
		<StaticBootstrapContext.Provider value={useMemoStaticProps}>
			{children}
		</StaticBootstrapContext.Provider>
	);
};
